/**
 * Pure questions about how keys are wired into the draft graph.
 *
 * No React here, so the rules the editor presents are unit-testable on their
 * own. The persisted shape is unchanged:
 *
 *   node.data.grantsKeys  – key ids a player picks up on arriving
 *   node.data.removesKeys – key ids a player loses on arriving
 *   edge.data.grantsKeys  – key ids a player picks up on picking the choice
 *   edge.data.removesKeys – key ids a player loses on picking the choice
 *   edge.data.key         – the one key a choice's visibility depends on
 *   edge.data.showIfKey   – visible ONLY to players who have that key
 *   edge.data.hideIfKey   – visible ONLY to players who do not have it
 *
 * The editor presents the last three as a single mode. Ids are compared as
 * strings on both sides, as canvasOps.removeKey does.
 */

export const VISIBILITY_MODES = Object.freeze({
    EVERYONE: 'everyone',
    HAS: 'has',
    LACKS: 'lacks',
});

const sameId = (a, b) => a !== null && a !== undefined && b !== null && b !== undefined
    && String(a) === String(b);

const listIncludes = (list, keyId) =>
    Array.isArray(list) && list.some((entry) => sameId(entry, keyId));

/**
 * Who can see a choice, as one of three modes.
 *
 * A flag with no key, or a key with neither flag, gates nothing — the player
 * (ChoicesBody.isChoiceVisible) shows the choice to everyone in both cases, so
 * that is what the editor reports too. Both flags at once cannot be produced by
 * the editor; the player checks `showIfKey` first, so 'has' wins here as well.
 *
 * @param {object} [edgeData]
 * @returns {'everyone'|'has'|'lacks'}
 */
export function visibilityMode(edgeData) {
    const data = edgeData || {};
    if (!data.key) return VISIBILITY_MODES.EVERYONE;
    if (data.showIfKey) return VISIBILITY_MODES.HAS;
    if (data.hideIfKey) return VISIBILITY_MODES.LACKS;
    return VISIBILITY_MODES.EVERYONE;
}

/**
 * The exact `updateChoice` patch that puts a choice into a mode. The key and
 * both flags always travel together, so no intermediate state is ever written.
 *
 * @param {'everyone'|'has'|'lacks'} mode
 * @param {string|null} [keyId] required for the gated modes
 * @returns {{key: string|null, showIfKey: boolean, hideIfKey: boolean}|null}
 *          null when the mode is unknown or a gated mode has no key
 */
export function patchForVisibilityMode(mode, keyId = null) {
    switch (mode) {
        case VISIBILITY_MODES.EVERYONE:
            return { key: null, showIfKey: false, hideIfKey: false };
        case VISIBILITY_MODES.HAS:
            return keyId ? { key: keyId, showIfKey: true, hideIfKey: false } : null;
        case VISIBILITY_MODES.LACKS:
            return keyId ? { key: keyId, showIfKey: false, hideIfKey: true } : null;
        default:
            return null;
    }
}

const nonEmpty = (list) => Array.isArray(list) && list.length > 0;

/**
 * What a choice does with keys, for drawing it.
 * @param {object} [edgeData]
 * @returns {{grants: boolean, removes: boolean, gate: 'has'|'lacks'|null}}
 */
export function edgeKeyRoles(edgeData) {
    const data = edgeData || {};
    const mode = visibilityMode(data);
    return {
        grants: nonEmpty(data.grantsKeys),
        removes: nonEmpty(data.removesKeys),
        gate: mode === VISIBILITY_MODES.EVERYONE ? null : mode,
    };
}

/**
 * What a situation does with keys, for drawing it.
 * @param {object} [nodeData]
 * @returns {{grants: boolean, removes: boolean}}
 */
export function nodeKeyRoles(nodeData) {
    const data = nodeData || {};
    return { grants: nonEmpty(data.grantsKeys), removes: nonEmpty(data.removesKeys) };
}

/** @param {object} [nodeData] */
export function nodeGrantsAnyKey(nodeData) {
    return nodeKeyRoles(nodeData).grants;
}

export const KEY_LIST_FIELDS = Object.freeze({
    GRANTS: 'grantsKeys',
    REMOVES: 'removesKeys',
});

const otherListField = (field) =>
    field === KEY_LIST_FIELDS.GRANTS ? KEY_LIST_FIELDS.REMOVES : KEY_LIST_FIELDS.GRANTS;

/**
 * The patch that adds a key to a situation's or choice's picked-up or
 * taken-away list.
 *
 * One place cannot both give and take the same key, so adding it to one list
 * moves it out of the other, in the same patch.
 *
 * @param {object} data node.data or edge.data
 * @param {'grantsKeys'|'removesKeys'} field
 * @param {string} keyId
 * @returns {object|null} null when there is nothing to change
 */
export function patchForAddingKey(data, field, keyId) {
    if (!keyId || (field !== KEY_LIST_FIELDS.GRANTS && field !== KEY_LIST_FIELDS.REMOVES)) return null;
    const current = Array.isArray(data?.[field]) ? data[field] : [];
    const other = otherListField(field);
    const otherList = Array.isArray(data?.[other]) ? data[other] : [];

    const patch = {};
    if (!listIncludes(current, keyId)) patch[field] = [...current, keyId];
    if (listIncludes(otherList, keyId)) {
        patch[other] = otherList.filter((entry) => !sameId(entry, keyId));
    }
    return Object.keys(patch).length > 0 ? patch : null;
}

/**
 * The patch that drops a key from one of the two lists.
 * @returns {object|null} null when the key is not in the list
 */
export function patchForRemovingKey(data, field, keyId) {
    if (field !== KEY_LIST_FIELDS.GRANTS && field !== KEY_LIST_FIELDS.REMOVES) return null;
    const current = Array.isArray(data?.[field]) ? data[field] : [];
    if (!listIncludes(current, keyId)) return null;
    return { [field]: current.filter((entry) => !sameId(entry, keyId)) };
}

/**
 * Everything in the graph that touches one key.
 *
 * @param {{nodes?: object[], edges?: object[]}} graph
 * @param {string} keyId
 * @returns {{
 *   grantedByNodes: object[], grantedByEdges: object[],
 *   removedByNodes: object[], removedByEdges: object[],
 *   hasEdges: object[], lacksEdges: object[],
 * }}
 */
export function keyUsage(graph, keyId) {
    const { nodes = [], edges = [] } = graph || {};
    const usage = {
        grantedByNodes: [],
        grantedByEdges: [],
        removedByNodes: [],
        removedByEdges: [],
        hasEdges: [],
        lacksEdges: [],
    };

    nodes.forEach((node) => {
        if (listIncludes(node?.data?.grantsKeys, keyId)) usage.grantedByNodes.push(node);
        if (listIncludes(node?.data?.removesKeys, keyId)) usage.removedByNodes.push(node);
    });

    edges.forEach((edge) => {
        const data = edge?.data || {};
        if (listIncludes(data.grantsKeys, keyId)) usage.grantedByEdges.push(edge);
        if (listIncludes(data.removesKeys, keyId)) usage.removedByEdges.push(edge);
        if (sameId(data.key, keyId)) {
            const mode = visibilityMode(data);
            if (mode === VISIBILITY_MODES.HAS) usage.hasEdges.push(edge);
            else if (mode === VISIBILITY_MODES.LACKS) usage.lacksEdges.push(edge);
        }
    });

    return usage;
}

export const KEY_WARNINGS = Object.freeze({
    NEVER_GRANTED: 'never-granted',
    NEVER_USED: 'never-used',
});

/**
 * Soft warnings for the Keys panel. These are advice, not validity errors: they
 * must never be fed into checkValidity, which blocks publishing.
 *
 * @param {ReturnType<typeof keyUsage>} usage
 * @returns {string[]}
 */
export function keyWarnings(usage) {
    const u = usage || {};
    const warnings = [];
    const granted = (u.grantedByNodes?.length || 0) + (u.grantedByEdges?.length || 0);
    const used = (u.hasEdges?.length || 0) + (u.lacksEdges?.length || 0);
    if (granted === 0) warnings.push(KEY_WARNINGS.NEVER_GRANTED);
    if (used === 0) warnings.push(KEY_WARNINGS.NEVER_USED);
    return warnings;
}

/**
 * The ids to leave at full strength while one key is being inspected: the
 * situations that give it or take it away, and the choices that give it, take
 * it away or depend on it.
 *
 * @param {{nodes?: object[], edges?: object[]}} graph
 * @param {string} keyId
 * @returns {{nodeIds: Set<string>, edgeIds: Set<string>}}
 */
export function highlightSets(graph, keyId) {
    const usage = keyUsage(graph, keyId);
    const nodeIds = new Set(
        [...usage.grantedByNodes, ...usage.removedByNodes].map((node) => String(node.id))
    );
    const edgeIds = new Set(
        [...usage.grantedByEdges, ...usage.removedByEdges, ...usage.hasEdges, ...usage.lacksEdges]
            .map((edge) => String(edge.id))
    );
    return { nodeIds, edgeIds };
}

/**
 * @param {{id: string, name: string}[]} [keys]
 * @param {string} keyId
 */
export function keyName(keys, keyId) {
    const key = Array.isArray(keys) ? keys.find((k) => sameId(k?.id, keyId)) : null;
    return key?.name || 'Unknown key';
}
